// DashboardStats.js
import React from 'react';

const DashboardStats = ({ stats, formatCurrency, isAdmin = false }) => {
  // Get user info from localStorage or props
  const getUserInfo = () => {
    const token = localStorage.getItem('authToken');
    if (!token) return { isStaff: false, isSuperuser: false };
    
    // Try to get user info from localStorage or JWT token
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      return {
        isStaff: userData.is_staff || false,
        isSuperuser: userData.is_superuser || false
      };
    } catch (error) {
      return { isStaff: false, isSuperuser: false };
    }
  };

  const userInfo = getUserInfo();
  const showCustomers = isAdmin || userInfo.isStaff || userInfo.isSuperuser;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {/* Total Quotations Card */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0 bg-blue-100 p-3 rounded-lg">
            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Total Quotations</p>
            <p className="text-2xl font-semibold text-gray-800">{stats.total_quotations}</p>
          </div>
        </div>
      </div>

      {/* Total Customers Card - Conditionally Rendered */}
      {showCustomers && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Customers</p>
              <p className="text-2xl font-semibold text-gray-800">{stats.total_users}</p>
            </div>
          </div>
        </div>
      )}

      {/* Total Value Card */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0 bg-purple-100 p-3 rounded-lg">
            <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Total Value</p>
            <p className="text-2xl font-semibold text-gray-800">
              {formatCurrency(stats.total_estimated_value)}
            </p>
          </div>
        </div>
      </div>

      {/* Today's Quotations Card */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0 bg-yellow-100 p-3 rounded-lg">
            <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Today's Quotations</p>
            <p className="text-2xl font-semibold text-gray-800">{stats.today_quotations}</p>
          </div>
        </div>
      </div>

      {/* Alternative Card for Regular Users - Shows Pending Quotations */}
      {!showCustomers && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-orange-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Review</p>
              <p className="text-2xl font-semibold text-gray-800">
                {stats.pending_quotations || 0}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardStats;